import { CheckCheck, BadgeCheck, Phone } from 'lucide-react';
import { useCompany } from '@/hooks/useCompany';

interface WhatsAppPreviewProps {
  content: string;
}

// Sample placeholders for preview
const sampleData: Record<string, string> = {
  '{nome}': 'João Silva',
  '{customer_name}': 'João Silva',
  '{valor}': 'R$ 1.250,00',
  '{vencimento}': '15/02/2026',
  '{billing_due_date}': '15/02/2026',
  '{link_pagamento}': 'https://pay.digisend.com.br/abc123',
  '{billing_url}': 'https://pay.digisend.com.br/abc123',
  '{company_name}': 'Digisend',
  '{company_phone}': '(11) 99999-9999',
};

function replacePlaceholders(text: string): string {
  let result = text;
  Object.entries(sampleData).forEach(([key, value]) => {
    result = result.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
  });
  return result;
}

export function WhatsAppPreview({ content }: WhatsAppPreviewProps) {
  const { data: company } = useCompany();
  
  const companyName = company?.trade_name || company?.name || 'Digisend';
  const companyLogo = company?.logo_url;
  
  const previewContent = replacePlaceholders(content);
  const currentDate = new Date().toLocaleDateString('pt-BR', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div className="bg-[#e5ddd5] rounded-xl p-4 max-w-sm mx-auto shadow-inner">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 bg-[#075e54] -mx-4 -mt-4 p-3 rounded-t-xl">
        {/* Company logo - round */}
        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-white">
          {companyLogo ? (
            <img src={companyLogo} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[#25d366] flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-white font-medium text-sm">{companyName}</p>
            {/* Blue verified badge */}
            <BadgeCheck className="w-4 h-4 text-[#53bdeb] fill-[#53bdeb]" />
          </div>
          <p className="text-white/70 text-xs">Conta comercial</p>
        </div>
      </div>

      {/* Date chip */}
      <div className="flex justify-center mb-3">
        <span className="bg-white/90 text-xs text-muted-foreground px-3 py-1 rounded-full shadow-sm">
          {currentDate}
        </span>
      </div>

      {/* Security notice */}
      <div className="bg-[#fcf4cb] border border-[#e8d48a] rounded-lg p-2 mb-3 text-xs text-[#54656f]">
        <p>Esta empresa usa um serviço seguro da empresa meta para gerenciar esta conversa. Toque para saber mais.</p>
      </div>

      {/* Message bubble */}
      <div className="bg-white rounded-lg p-3 shadow-sm relative ml-8">
        <div className="whitespace-pre-wrap text-sm text-[#111b21] leading-relaxed">
          {previewContent || 'Digite o conteúdo da mensagem para ver a pré-visualização...'}
        </div>
        <div className="flex items-center justify-end gap-1 mt-2">
          <span className="text-[11px] text-[#667781]">
            {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <CheckCheck className="w-4 h-4 text-[#53bdeb]" />
        </div>
        {/* Bubble tail */}
        <div className="absolute -left-2 top-0 w-0 h-0 border-t-[10px] border-t-white border-l-[10px] border-l-transparent" />
      </div>
    </div>
  );
}
